import React, { useState } from 'react';
import { FareData, ModeFares, ExchangeRates, Currency, TransportMode } from '../../types/fare';
import { formatCurrencyAmount } from '../../services/fareService';
import { getModeName, getModeEmoji } from '../../utils/fareCalculationUtils';
import { MapPin } from 'lucide-react';

interface FareCardProps {
  fare: FareData | ModeFares;
  distanceKm: number;
  exchangeRates: ExchangeRates;
  mode?: TransportMode; // Optional override for ModeFares
}

export default function FareCard({ fare, distanceKm, exchangeRates, mode }: FareCardProps) {
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>('LKR');

  const currencies: Currency[] = ['LKR', 'USD', 'GBP', 'EUR', 'CNY', 'RUB'];

  // Safety check
  if (!fare || !fare.min || !fare.max || !fare.base) {
    return null;
  }

  // Determine mode and ceiling
  const fareMode = mode || ('mode' in fare ? fare.mode : 'tuk');
  const ceiling = 'ceiling' in fare ? fare.ceiling : fare.max * 1.2; // Estimate ceiling as 20% above max for ModeFares

  const minFormatted = formatCurrencyAmount(fare.min, selectedCurrency, exchangeRates);
  const maxFormatted = formatCurrencyAmount(fare.max, selectedCurrency, exchangeRates);
  const baseFormatted = formatCurrencyAmount(fare.base, selectedCurrency, exchangeRates);
  const ceilingFormatted = formatCurrencyAmount(ceiling, selectedCurrency, exchangeRates);

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white rounded-xl p-3 md:p-6 shadow-lg">
      {/* Mode badge */}
      <div className="flex items-center justify-between mb-2 md:mb-4">
        <div className="inline-flex items-center gap-1 md:gap-2 bg-white/20 px-2 py-1 md:px-4 md:py-2 rounded-full">
          <span className="text-base md:text-2xl">{getModeEmoji(fareMode)}</span>
          <span className="font-semibold text-sm md:text-lg">{getModeName(fareMode)}</span>
        </div>
        
        {/* Currency selector */}
        <select
          value={selectedCurrency}
          onChange={(e) => setSelectedCurrency(e.target.value as Currency)}
          className="bg-white/10 border border-white/30 rounded-lg px-2 py-0.5 md:px-3 md:py-1.5 text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
        >
          {currencies.map(curr => (
            <option key={curr} value={curr} className="bg-gray-800">
              {curr}
            </option>
          ))}
        </select>
      </div>

      {/* Price range */}
      <div className="mb-2 md:mb-6">
        <div className="text-[10px] md:text-sm text-white/70 mb-0.5 md:mb-1">Expected Fare Range</div>
        <div className="text-xl md:text-4xl font-bold leading-tight">
          {minFormatted} - {maxFormatted}
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 pt-2 md:pt-4 border-t border-white/20">
        <div>
          <div className="text-[10px] md:text-xs text-white/70 mb-0.5 md:mb-1">Average</div>
          <div className="text-xs md:text-lg font-semibold">{baseFormatted}</div>
        </div>
        <div>
          <div className="text-[10px] md:text-xs text-white/70 mb-0.5 md:mb-1">Ceiling</div>
          <div className="text-xs md:text-lg font-semibold">{ceilingFormatted}</div>
        </div>
        <div>
          <div className="text-[10px] md:text-xs text-white/70 mb-0.5 md:mb-1">Distance</div>
          <div className="text-xs md:text-lg font-semibold flex items-center gap-0.5 md:gap-1">
            <MapPin size={12} className="md:w-4 md:h-4" />
            {distanceKm} km
          </div>
        </div>
      </div>
    </div>
  );
}

